import classNames from "classnames";
import type { SelectHTMLAttributes } from "react";
import React, { forwardRef, useLayoutEffect, useRef, useState } from "react";

import Icon from "@atoms/Icon";

export interface IOption<T extends string | number> {
  name: string;
  value: T;
}

interface IProps<T extends string | number> extends SelectHTMLAttributes<HTMLSelectElement> {
  className?: string;
  options: IOption<T>[];
  isError?: boolean;
  variant?: "primary" | "secondary" | "tertiary";
  as?: "default" | "form" | "square" | "view";
  label?: string;
}

function Select<T extends string | number>(
  {
    options,
    name,
    variant = "primary",
    className = "",
    as = "default",
    label,
    value,
    id,
    isError = false,
    ...props
  }: IProps<T>,
  ref: React.Ref<HTMLSelectElement>,
) {
  const measureRef = useRef<HTMLSpanElement>(null);

  const [contentWidth, setContentWidth] = useState(0);

  const selectClassName = classNames(
    "peer cursor-pointer py-1.5 dark:border-white",
    as === "default" &&
      "text-subtitle pc:text-base rounded-full border border-black pr-10 pl-3.5 font-normal outline-hidden",
    as === "form" && "disabled:text-grey w-full truncate border-b border-solid border-black pr-8 caret-black",
    as === "square" && [
      variant === "primary" &&
        "text-subtitle pc:text-base border-dark-grey w-full border pr-10 pl-2 font-normal outline-hidden pc:h-7.5 h-10 pc:text-base text-base!",
      variant === "secondary" && "border-dark-grey border  leading-none px-2 py-1 font-normal capitalize",
    ],
    className,
    as === "view" && "border-dark-grey border px-2 py-1 font-normal capitalize",
    isError && "border-red! dark:border-red!",
  );

  // 비제어로 쓰는 곳(value 미지정)은 브라우저가 첫 옵션을 고르므로 그걸 기준으로 잡는다
  const currentValue = value ?? props.defaultValue ?? options?.[0]?.value;
  const selectedName = options?.find((option) => String(option.value) === String(currentValue))?.name ?? "";

  useLayoutEffect(() => {
    if (variant === "primary" || !measureRef.current) return;
    setContentWidth(measureRef.current.offsetWidth + 16);
  }, [variant, selectedName]);

  return (
    <div
      className={classNames(
        "relative cursor-pointer",
        as === "default" && "flex w-fit items-center",
        as === "form" && "flex flex-col gap-1",
        as === "square" && "flex items-center",
        as === "view" && "flex items-center",
      )}
    >
      {label ? (
        <label className="cursor-pointer" htmlFor={id}>
          {label}
        </label>
      ) : null}

      <span
        aria-hidden
        className={classNames(selectClassName, "invisible absolute whitespace-pre")}
        ref={measureRef}
        style={{ width: "auto" }}
      >
        {selectedName}
      </span>

      <select
        className={selectClassName}
        id={id}
        key={name}
        name={name}
        ref={ref}
        style={variant === "secondary" && contentWidth ? { width: `${contentWidth}px` } : undefined}
        value={value}
        {...props}
      >
        {options?.map((option) => (
          <option disabled={option.value === ""} key={option.value} value={option.value}>
            {option.name}
          </option>
        ))}
      </select>
      <Icon
        className={classNames(
          "pointer-events-none absolute dark:text-white",
          as === "default" && "right-3",
          as === "form" && "top-6 right-0.5",
          as === "square" && [
            variant === "primary" && "pc:top-2 pc:peer-open:top-1 top-3 right-2 peer-open:top-2 peer-open:rotate-180",
            variant === "secondary" &&
              "pc:top-1 pc:peer-open:top-1 top-1.75 right-2 peer-open:top-0.5 peer-open:rotate-180",
          ],
          as === "view" && "hidden",
        )}
        name={as === "square" ? "ArrowDown" : "Select"}
        width={as === "square" ? "10px" : "11px"}
      />
    </div>
  );
}

export default forwardRef(Select);
